import { useState } from 'react';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API_URL = 'https://songmoment.onrender.com';

export default function LoginModal({ onClose }) {
  const { login, signup } = useApp();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignup, setIsSignup] = useState(true);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required');
      return;
    }
    if (isSignup && !username.trim()) {
      setError('Username is required');
      return;
    }
    setLoading(true);
    setError('');
    setEmailNotVerified(false);
    try {
      if (isSignup) {
        const result = await signup(
          email.trim().toLowerCase(),
          password,
          username.trim().toLowerCase().replace(/\s+/g, '_')
        );
        if (result?.emailConfirmationRequired) {
          setSignupSuccess(true);
          return;
        }
      } else {
        await login(email.trim().toLowerCase(), password);
      }
      onClose();
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.emailNotVerified) {
        setEmailNotVerified(true);
        setError(errData.error);
      } else {
        setError(errData?.error || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: email.trim().toLowerCase() });
      setVerificationSent(true);
    } catch (err) {
      console.error(err);
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(212,83,126,0.3)',
    borderRadius: 10, padding: '11px 14px',
    fontSize: 14, color: '#f5e6ed',
    marginBottom: 10, boxSizing: 'border-box',
    outline: 'none',
  };

  // ── Signup success screen ──
  if (signupSuccess) return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#0e0a0c', border: '0.5px solid rgba(212,83,126,0.3)',
        borderRadius: 18, width: '100%', maxWidth: 380, padding: 32, textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 24, color: '#f5e6ed', marginBottom: 10, letterSpacing: '0.05em' }}>
          Check your inbox!
        </h2>
        <p style={{ fontSize: 13, color: 'rgba(245,230,237,0.5)', marginBottom: 16, lineHeight: 1.6 }}>
          We sent a verification link to <strong style={{ color: '#ED93B1' }}>{email}</strong>. Click it to activate your account.
        </p>
        <div style={{
          background: 'rgba(212,83,126,0.08)',
          border: '0.5px solid rgba(212,83,126,0.2)',
          borderRadius: 10, padding: '10px 14px',
          fontSize: 12, color: 'rgba(245,230,237,0.5)',
          marginBottom: 24, lineHeight: 1.6, textAlign: 'left',
        }}>
          💡 Don't see it? Check your <strong style={{ color: '#ED93B1' }}>spam/junk folder</strong>. Add our email to contacts so future emails land in your inbox!
        </div>
        <button onClick={onClose} style={{
          width: '100%', background: '#D4537E', color: '#fff',
          borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600,
          border: 'none', cursor: 'pointer',
        }}>Got it!</button>
      </div>
    </div>
  );

  // ── Main login/signup screen ──
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-in" style={{
        background: '#0e0a0c', border: '0.5px solid rgba(212,83,126,0.3)',
        borderRadius: 18, width: '100%', maxWidth: 380, padding: 28,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'linear-gradient(135deg, #D4537E, #993556)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, margin: '0 auto 12px', color: '#fff',
          }}>♪</div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300, fontSize: 24,
            letterSpacing: '0.05em', color: '#f5e6ed', marginBottom: 6,
          }}>
            {isSignup ? 'Join EchoMoments' : 'Welcome Back'}
          </h2>
          <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)', letterSpacing: '0.03em' }}>
            {isSignup ? 'Create an account to share moments' : 'Sign in to continue'}
          </p>
        </div>

        {/* Toggle tabs */}
        <div style={{
          display: 'flex', background: 'rgba(255,255,255,0.04)',
          borderRadius: 10, marginBottom: 20, padding: 3, gap: 3,
        }}>
          {['Sign Up', 'Log In'].map((label, i) => {
            const active = isSignup ? i === 0 : i === 1;
            return (
              <button key={label}
                onClick={() => { setIsSignup(i === 0); setError(''); setEmailNotVerified(false); }}
                style={{
                  flex: 1, padding: '8px 0', borderRadius: 8,
                  fontSize: 12, fontWeight: 600, letterSpacing: '0.05em',
                  background: active ? '#D4537E' : 'transparent',
                  color: active ? '#fff' : 'rgba(245,230,237,0.4)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}>
                {label}
              </button>
            );
          })}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {isSignup && (
            <input type="text" value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Choose a username..."
              autoFocus style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#D4537E'}
              onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.3)'}
            />
          )}
          <input type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email address..."
            autoFocus={!isSignup} style={inputStyle}
            onFocus={e => e.target.style.borderColor = '#D4537E'}
            onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.3)'}
          />
          <input type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password..."
            style={{ ...inputStyle, marginBottom: error ? 6 : 10 }}
            onFocus={e => e.target.style.borderColor = '#D4537E'}
            onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.3)'}
          />

          {error && (
            <p style={{ fontSize: 12, color: '#f87171', marginBottom: 8 }}>{error}</p>
          )}

          {/* Email not verified — show resend button */}
          {emailNotVerified && (
            <div style={{ marginBottom: 10 }}>
              {verificationSent ? (
                <p style={{ fontSize: 12, color: '#ED93B1', textAlign: 'center' }}>✓ Verification email sent!</p>
              ) : (
                <button type="button" onClick={handleResendVerification} style={{
                  width: '100%', background: 'rgba(212,83,126,0.1)',
                  border: '0.5px solid rgba(212,83,126,0.4)',
                  borderRadius: 8, padding: '8px', fontSize: 12,
                  color: '#ED93B1', cursor: 'pointer',
                }}>
                  📬 Resend verification email
                </button>
              )}
            </div>
          )}

          <button type="submit"
            disabled={!email.trim() || !password.trim() || (isSignup && !username.trim()) || loading}
            style={{
              width: '100%', background: '#D4537E', color: '#fff',
              borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600,
              border: 'none', cursor: 'pointer', marginBottom: 10,
              opacity: (!email.trim() || !password.trim() || (isSignup && !username.trim()) || loading) ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}>
            {loading ? (isSignup ? 'Creating account...' : 'Signing in...') : (isSignup ? 'Create Account' : 'Let me in')}
          </button>
        </form>

        <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.25)', textAlign: 'center', marginTop: 14, letterSpacing: '0.03em' }}>
          {isSignup ? 'Already have an account? Switch to Log In above.' : 'New here? Switch to Sign Up above.'}
        </p>
      </div>
    </div>
  );
}